import React, { useEffect, useMemo, useRef, useState } from "react";
import { FaEdit, FaTrash, FaBoxes, FaSyncAlt } from "react-icons/fa";
import { toast } from "react-toastify";
import "./Medicine.css";
import MedicineModal from "../AllComponents/Inventory/MedicineModal";
import ConfirmDeleteModal from "../AllComponents/Inventory/ConfirmDeleteModal";
import UpdateStockModal from "../AllComponents/Inventory/UpdateStockModal";
import PaginationComponent from "../AllComponents/Shared/PaginationComponent";
import CustomDropdown from "../resuableComponents/CustomDropdown";
import {
  getMedicines,
  searchMedicines,
  createMedicine as apiCreateMedicine,
  updateMedicine as apiUpdateMedicine,
  deleteMedicine as apiDeleteMedicine,
  getBranchStock,
  updateBranchStock,
  initBranchStock,
} from "../../api/admin/inventory";
import { getBranches } from "../../api/admin/branch";
import { readAuthRole } from "../../utils/auth";

const readUserBranch = (user) => {
  const branch = user?.branch;
  if (!branch) return { id: "", name: "" };
  if (typeof branch === "string") return { id: "", name: branch };
  return { id: branch._id || branch.id || "", name: branch.name || "" };
};

const Medicine = () => {
  const { isAdmin, user } = useMemo(readAuthRole, []);
  const ownBranch = useMemo(() => (isAdmin ? { id: "", name: "" } : readUserBranch(user)), [isAdmin, user]);

  // ── Branch state ─────────────────────────────────────────────────────────
  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState("");
  const [selectedBranchName, setSelectedBranchName] = useState("");

  // ── Medicine catalog ──────────────────────────────────────────────────────
  const [medicines, setMedicines] = useState([]);
  const [medicinesLoading, setMedicinesLoading] = useState(false);

  // ── Branch stock map: medicineId → { _id (BranchStock doc), stock } ───────
  const [stockMap, setStockMap] = useState({});
  const [stockLoading, setStockLoading] = useState(false);

  // ── Search ────────────────────────────────────────────────────────────────
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchTimeoutRef = useRef(null);

  // ── Pagination ────────────────────────────────────────────────────────────
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const limitOptions = [10, 20, 30, 50, 100];

  // ── Medicine modal ────────────────────────────────────────────────────────
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingMedicine, setEditingMedicine] = useState(null);

  // ── Delete modal ──────────────────────────────────────────────────────────
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // ── Stock edit modal ──────────────────────────────────────────────────────
  const [stockEditTarget, setStockEditTarget] = useState(null);
  const [savingStock, setSavingStock] = useState(false);

  // ── Data fetchers ─────────────────────────────────────────────────────────
  const fetchMedicines = async () => {
    setMedicinesLoading(true);
    try {
      const { data } = await getMedicines();
      const list = Array.isArray(data) ? data : data?.result || [];
      setMedicines(list.map((m) => ({ ...m, id: m.id || m._id })));
    } catch {
      toast.error("Failed to load medicines.");
    } finally {
      setMedicinesLoading(false);
    }
  };

  const fetchBranchStock = async (branchId) => {
    setStockLoading(true);
    try {
      const params = branchId ? { branchId } : undefined;
      const { data } = await getBranchStock(params);
      const list = data?.result || [];
      const map = {};
      for (const entry of list) {
        const medId = String(entry.medicine?._id || entry.medicine);
        map[medId] = { _id: entry._id, stock: entry.stock ?? 0 };
      }
      setStockMap(map);
      const populated = list.find((entry) => entry.branch && typeof entry.branch === "object")?.branch;
      if (populated?.name) {
        setSelectedBranchName((current) => current || populated.name);
      }
      if (populated?._id) {
        setSelectedBranchId((current) => current || populated._id);
      }
    } catch {
      setStockMap({});
    } finally {
      setStockLoading(false);
    }
  };

  // ── Initial load ──────────────────────────────────────────────────────────
  useEffect(() => {
    fetchMedicines();

    if (isAdmin) {
      (async () => {
        try {
          const { data } = await getBranches();
          const list = data?.result || [];
          setBranches(list);
          if (list.length > 0) {
            setSelectedBranchId(list[0]._id);
            setSelectedBranchName(list[0].name);
          }
        } catch {
          toast.error("Failed to load branches.");
        }
      })();
    } else {
      // Non-admin: server resolves their branch from token
      if (ownBranch.id) setSelectedBranchId(ownBranch.id);
      if (ownBranch.name) setSelectedBranchName(ownBranch.name);
      fetchBranchStock(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Reload stock when branch selection changes (admin) ────────────────────
  useEffect(() => {
    if (isAdmin && selectedBranchId) {
      fetchBranchStock(selectedBranchId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBranchId, isAdmin]);

  // ── Debounced search ──────────────────────────────────────────────────────
  useEffect(() => {
    clearTimeout(searchTimeoutRef.current);
    const query = search.trim();
    if (!query) {
      setSearchResults([]);
      setSearchLoading(false);
      return;
    }
    setSearchLoading(true);
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const { data } = await searchMedicines(query);
        const raw = Array.isArray(data) ? data : data?.results || data?.result || [];
        setSearchResults(raw.map((m) => ({ ...m, id: m.id || m._id })));
      } catch {
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 600);
    return () => clearTimeout(searchTimeoutRef.current);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [search, limit]);

  // ── Derived display list: merge catalog + stock ───────────────────────────
  const baseList = search.trim() && !medicinesLoading ? searchResults : medicines;

  const displayedRows = useMemo(
    () =>
      baseList.map((med) => {
        const medId = String(med.id || med._id);
        const entry = stockMap[medId];
        return {
          ...med,
          id: medId,
          price: med.price !== undefined ? Number(med.price) : Number(med.cost) || 0,
          stock: entry?.stock ?? 0,
          stockDocId: entry?._id,
        };
      }),
    [baseList, stockMap]
  );

  const pageItems = useMemo(
    () => displayedRows.slice((page - 1) * limit, page * limit),
    [displayedRows, page, limit]
  );

  // ── Summary stats (based on full medicine list + current stock map) ────────
  const allRows = useMemo(
    () =>
      medicines.map((med) => {
        const medId = String(med.id || med._id);
        const entry = stockMap[medId];
        return {
          price: med.price !== undefined ? Number(med.price) : Number(med.cost) || 0,
          stock: entry?.stock ?? 0,
        };
      }),
    [medicines, stockMap]
  );

  const lowStockCount = useMemo(() => allRows.filter((r) => r.stock <= 5).length, [allRows]);
  const totalStockValue = useMemo(
    () => allRows.reduce((sum, r) => sum + r.price * r.stock, 0),
    [allRows]
  );

  // ── Handlers: medicine CRUD ───────────────────────────────────────────────
  const handleSaveFromModal = async (payload) => {
    const backendPayload = { name: payload.name, cost: payload.price, qty: payload.qty };
    try {
      if (editingMedicine) {
        await apiUpdateMedicine(editingMedicine.id, backendPayload);
        toast.success("Medicine updated!");
      } else {
        if (payload.stock !== undefined) backendPayload.stock = payload.stock;
        await apiCreateMedicine(backendPayload);
        toast.success("Medicine added!");
      }
      setIsModalOpen(false);
      setEditingMedicine(null);
      await fetchMedicines();
      // Re-seed stock map so the new medicine row shows correct stock
      if (selectedBranchId || !isAdmin) {
        await fetchBranchStock(selectedBranchId || null);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to save medicine.");
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await apiDeleteMedicine(deleteTarget.id);
      setMedicines((prev) => prev.filter((m) => m.id !== deleteTarget.id));
      setSearchResults((prev) => prev.filter((m) => m.id !== deleteTarget.id));
      setStockMap((prev) => {
        const next = { ...prev };
        delete next[deleteTarget.id];
        return next;
      });
      toast.success("Medicine deleted!");
    } catch {
      toast.error("Failed to delete medicine.");
    } finally {
      setIsDeleting(false);
      setDeleteTarget(null);
    }
  };

  // ── Handlers: stock ───────────────────────────────────────────────────────
  const openStockEdit = (row) => {
    setStockEditTarget({
      _id: row.stockDocId,
      branch: { _id: selectedBranchId, name: selectedBranchName },
      medicine: { _id: row.id, name: row.name, qty: row.qty, cost: row.price },
      stock: row.stock,
    });
  };

  const handleSaveStock = async ({ branchId, medicineId, stock }) => {
    setSavingStock(true);
    try {
      await updateBranchStock({ branchId, medicineId, stock });
      toast.success("Stock updated!");
      setStockMap((prev) => ({
        ...prev,
        [String(medicineId)]: { ...prev[String(medicineId)], stock },
      }));
      setStockEditTarget(null);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to update stock.");
    } finally {
      setSavingStock(false);
    }
  };

  const handleSeedBranch = async () => {
    if (!selectedBranchId) return;
    try {
      const { data } = await initBranchStock(selectedBranchId);
      toast.success(
        `Initialized — ${data?.upserted ?? 0} new entries, ${data?.matched ?? 0} already existed.`
      );
      fetchBranchStock(selectedBranchId);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to initialize stock.");
    }
  };

  const handleBranchChange = (value) => {
    setSelectedBranchId(value);
    setSelectedBranchName(branches.find((b) => b._id === value)?.name || "");
  };

  const isLoading = medicinesLoading || stockLoading;

  return (
    <div className="med-surface">
      <div className="med-shell">
        {/* ── Header ─────────────────────────────────────────────────────── */}
        <header className="med-hero">
          <div className="med-hero-copy">
            <p className="med-chip">Medicine &amp; Inventory</p>
            <h1>
              Medicines
              <span> &amp; branch stock.</span>
            </h1>
            <p>
              {isAdmin
                ? "Manage the medicine catalog and adjust per-branch stock levels in one place."
                : selectedBranchName
                  ? `Stock on this page is for ${selectedBranchName}. Saving a change updates only this branch.`
                  : "Stock on this page is for your branch. Saving a change updates only that branch."}
            </p>
          </div>

          {/* Summary cards — shown only once a branch is selected */}
          {(selectedBranchId || !isAdmin) && (
            <div className="med-summary">
              <div className="med-summary-card">
                <span className="med-summary-label">Medicines</span>
                <span className="med-summary-value">{medicines.length}</span>
              </div>
              <div className={`med-summary-card ${lowStockCount > 0 ? "med-summary-alert" : ""}`}>
                <span className="med-summary-label">Low stock (&le;&nbsp;5)</span>
                <span className="med-summary-value">{lowStockCount}</span>
              </div>
              <div className="med-summary-card">
                <span className="med-summary-label">Stock value</span>
                <span className="med-summary-value">
                  &#8377;&nbsp;{totalStockValue.toLocaleString("en-IN")}
                </span>
              </div>
            </div>
          )}
        </header>

        {/* ── Controls ───────────────────────────────────────────────────── */}
        <section className="med-controls">
          {!isAdmin && selectedBranchName && (
            <div className="med-own-branch" role="status">
              Your branch: <strong>{selectedBranchName}</strong>
            </div>
          )}
          {/* Branch selector — admin only */}
          {isAdmin && branches.length > 0 && (
            <div className="med-control-branch">
              <CustomDropdown
                label="Branch"
                value={selectedBranchId}
                onChange={handleBranchChange}
                options={branches.map((b) => ({ value: b._id, label: b.name }))}
              />
            </div>
          )}

          {/* Search */}
          <div className="med-search-block med-control-search">
            <div className="med-search-field">
              <svg className="med-search-icon" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                  clipRule="evenodd"
                />
              </svg>
              <input
                type="text"
                placeholder="Search by name…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          {/* Action buttons */}
          <div className="med-control-actions">
            <button className="med-primary" type="button" onClick={() => { setEditingMedicine(null); setIsModalOpen(true); }}>
              <span>+</span> Add medicine
            </button>
            {isAdmin && selectedBranchId && (
              <button
                type="button"
                className="med-ghost"
                onClick={handleSeedBranch}
                title="Create missing stock entries (stock=0) for this branch"
              >
                Initialize missing
              </button>
            )}
            <button
              type="button"
              className="med-ghost"
              onClick={() => { fetchMedicines(); if (selectedBranchId || !isAdmin) fetchBranchStock(selectedBranchId || null); }}
              title="Refresh"
            >
              <FaSyncAlt />
            </button>
          </div>
        </section>

        {/* ── Table ──────────────────────────────────────────────────────── */}
        <section className="med-table-wrapper">
          <table className="med-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Pack</th>
                <th>Price (Rs)</th>
                <th>
                  Stock
                  {selectedBranchName && (
                    <span className="med-th-branch">&nbsp;·&nbsp;{selectedBranchName}</span>
                  )}
                </th>
                <th className="med-actions-col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                Array.from({ length: limit }).map((_, i) => (
                  <tr key={`sk-${i}`} className="med-skeleton-row">
                    <td><div className="med-skeleton-cell" /></td>
                    <td><div className="med-skeleton-cell" /></td>
                    <td><div className="med-skeleton-cell" /></td>
                    <td><div className="med-skeleton-cell" style={{ width: 40 }} /></td>
                    <td className="med-actions">
                      <div className="med-skeleton-actions">
                        <div className="med-skeleton-button" />
                        <div className="med-skeleton-button" />
                        <div className="med-skeleton-button" />
                      </div>
                    </td>
                  </tr>
                ))
              ) : displayedRows.length === 0 ? (
                <tr>
                  <td colSpan={5} className="med-empty">
                    {search ? "No medicines match your search." : "No medicines added yet."}
                  </td>
                </tr>
              ) : (
                pageItems.map((row) => (
                  <tr key={row.id}>
                    <td style={{ fontWeight: 600 }}>{row.name}</td>
                    <td>{row.qty || "—"}</td>
                    <td>{row.price}</td>
                    <td>
                      <span className={`med-stock-pill ${row.stock <= 5 ? "low" : "ok"}`}>
                        {row.stock}
                      </span>
                    </td>
                    <td className="med-actions">
                      <button
                        type="button"
                        className="med-btn med-btn-edit"
                        title="Edit medicine details"
                        onClick={() => { setEditingMedicine(row); setIsModalOpen(true); }}
                        aria-label="Edit medicine"
                      >
                        <FaEdit />
                      </button>
                      <button
                        type="button"
                        className="med-btn med-btn-stock"
                        title="Edit stock for this branch"
                        onClick={() => openStockEdit(row)}
                        aria-label="Edit stock"
                        disabled={!selectedBranchId && isAdmin}
                      >
                        <FaBoxes />
                      </button>
                      <button
                        type="button"
                        className="med-btn med-btn-delete"
                        title="Delete medicine"
                        onClick={() => setDeleteTarget(row)}
                        aria-label="Delete medicine"
                      >
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </section>

        {displayedRows.length > limit && (
          <PaginationComponent
            total={displayedRows.length}
            limit={limit}
            limitOptions={limitOptions}
            activePage={page}
            onChangePage={setPage}
            onChangeLimit={(nextLimit) => { setLimit(nextLimit); setPage(1); }}
            className="med-pagination"
          />
        )}

        {/* ── Modals ──────────────────────────────────────────────────────── */}
        <MedicineModal
          isOpen={isModalOpen}
          mode={editingMedicine ? "edit" : "add"}
          initialValues={editingMedicine || undefined}
          onClose={() => { setIsModalOpen(false); setEditingMedicine(null); }}
          onSave={handleSaveFromModal}
        />

        <ConfirmDeleteModal
          isOpen={!!deleteTarget}
          title="Delete medicine"
          message={
            deleteTarget
              ? `Are you sure you want to delete "${deleteTarget.name}"? This will remove its stock records from all branches.`
              : ""
          }
          onCancel={() => { if (!isDeleting) setDeleteTarget(null); }}
          onConfirm={handleConfirmDelete}
          confirming={isDeleting}
        />

        <UpdateStockModal
          isOpen={!!stockEditTarget}
          record={stockEditTarget}
          submitting={savingStock}
          onClose={() => setStockEditTarget(null)}
          onSave={handleSaveStock}
        />
      </div>
    </div>
  );
};

export default Medicine;
